/**
 * ScoreboardScreen V9 - Simplified & Emotional
 * 
 * TRUE TO V9 MOCKUP DESIGN:
 * - No section headers (just rank-ordered list)
 * - Coaches above you: -{gap} with "in range" / "out of reach"
 * - Your row: distinct styling (bg-slate-800 border-2 border-slate-600)
 * - Coaches below you: +{cushion} with "can catch you" / "not a threat" / "finished"
 * 
 * COLORS (3 only):
 * - Green: Good for you (in range, not a threat, safe)
 * - Red: Bad for you (out of reach, can catch you, danger)
 * - Gray: Neutral (finished, inactive)
 * 
 * ICONS (4 only):
 * - 👑 Leader
 * - 🔥 High-impact player (15+ units)
 * - 🎯 Scout (all opponents)
 * - 📋 Your Field Report
 * 
 * PHASE 3.5 COMPLIANCE:
 * - Live Summary Layer for roster-based player info
 * - impact_remaining_units from live-state (NOT balance)
 * 
 * NAVIGATION (LOCKED):
 * - Opponent row → H2H Scout (/standings/:poolId/vs/:opponentUserId)
 * - Your row → Field Report (/pools/:poolId/field-report)
 * - Back → War Room (/)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Hooks
import { useLeaderboardPolling } from '../../hooks/useLeaderboardPolling';
import { useLiveSummary } from '../../hooks/useLiveSummary';
import { useAuth } from '../../context/AuthContext';

// =============================================================================
// ICONS (V9 spec)
// =============================================================================

const BackIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const RefreshIcon = ({ spinning = false }) => (
  <svg className={`w-6 h-6 ${spinning ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

// =============================================================================
// HELPERS (V9 spec)
// =============================================================================

// Only 🔥 for high impact (15+), nothing else
const getPlayerIcon = (units) => units >= 15 ? '🔥' : '';

/**
 * Build player info lines from live summary
 * 
 * LIVE/PENDING MODE:
 * - "🔥Mahomes (20) +1 on field" / "Chase (15) in locker room"
 * 
 * FINAL MODE:
 * - "5 of 7 hit (71%)" - Game plan results summary
 */
const buildPlayerInfo = (summary, isFinal = false) => {
  if (!summary) return ['loading...'];
  
  const { onField, locker, players, results } = summary;
  
  // FINAL MODE: Show game plan results
  if (isFinal && results && results.total > 0) {
    const { hits, total, misses, pushes } = results;
    const hitRate = Math.round((hits / total) * 100);
    const lines = [];
    
    // Main result line: "5 of 7 hit (71%)"
    lines.push(`${hits} of ${total} hit (${hitRate}%)`);
    
    // Optional: Show misses if any
    if (misses > 0) {
      lines.push(`${misses} missed${pushes > 0 ? `, ${pushes} push` : ''}`);
    }
    
    return lines;
  }
  
  // LIVE/PENDING MODE: Show active players
  const lines = [];
  
  // On Field
  if (onField > 0 && players?.onField?.length > 0) {
    const sorted = [...players.onField].sort((a, b) => b.units - a.units);
    const top = sorted[0];
    const icon = getPlayerIcon(top.units);
    const others = onField > 1 ? ` +${onField - 1}` : '';
    lines.push(`${icon}${top.name} (${top.units})${others} on field`);
  }
  
  // Locker Room
  if (locker > 0 && players?.locker?.length > 0) {
    const sorted = [...players.locker].sort((a, b) => b.units - a.units);
    const top = sorted[0];
    const icon = getPlayerIcon(top.units);
    const others = locker > 1 ? ` +${locker - 1}` : '';
    lines.push(`${icon}${top.name} (${top.units})${others} in locker room`);
  }
  
  // Fallback
  if (lines.length === 0) {
    lines.push('all finished');
  }
  
  return lines;
};

// =============================================================================
// FOOTER HUD - V9 Emotional Language
// =============================================================================

const FooterHUD = ({ you, coaches, summaries, liveStateEntries, mode }) => {
  if (!you) return null;
  
  const isHistorical = mode === 'historical';
  const leader = coaches.find(c => c.rank === 1);
  const leaderGap = leader ? leader.score - you.score : 0;
  
  // Get YOUR impact from live-state
  const myImpact = liveStateEntries?.[you.userId]?.impact_remaining_units ?? 0;
  
  // H2H PATTERN: Check if all coaches are resolved (no active players)
  // This handles the case where live-state returns 200 but pool is actually finished
  const allResolved = Object.keys(summaries).length > 0 && 
    Object.values(summaries).every(s => s.onField === 0 && s.locker === 0);
  
  // FINAL: Either HISTORICAL mode OR all players resolved
  const isFinal = isHistorical || allResolved;
  
  if (isFinal) {
    const narrative = you.rank === 1 
      ? "🏆 Final — You won!"
      : `Final — Finished #${you.rank}`;
    
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 z-40">
        <div className="text-center py-2.5 px-4 text-[14px] font-bold leading-tight bg-slate-800 text-slate-400">
          {narrative}
        </div>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="text-center flex-1">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Rank</p>
            <p className="text-xl font-black text-white">#{you.rank}</p>
          </div>
          <div className="text-center flex-1">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Score</p>
            <p className="text-xl font-mono font-black text-white">{you.score}</p>
          </div>
          {you.rank !== 1 && (
            <div className="text-center flex-1">
              <p className="text-[11px] font-bold text-slate-500 uppercase">Gap</p>
              <p className="text-xl font-mono font-black text-red-400">-{leaderGap}</p>
            </div>
          )}
          <div className="text-center flex-1">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Left</p>
            <p className="text-xl font-mono font-black text-emerald-400">{myImpact}</p>
          </div>
        </div>
      </div>
    );
  }
  
  // LIVE/PENDING: Emotional narrative
  const canCatchLeader = leaderGap <= myImpact && myImpact > 0;
  
  const ahead = coaches.filter(c => !c.isMe && c.score > you.score);
  const reachable = ahead.filter(c => (c.score - you.score) <= myImpact && myImpact > 0);
  const bestReachable = reachable.length > 0 
    ? reachable.reduce((best, c) => c.rank < best.rank ? c : best)
    : null;
  
  const behind = coaches.filter(c => !c.isMe && c.score < you.score);
  
  // Hunters = have active players AND can catch you
  const hunters = behind.filter(c => {
    const summary = summaries?.[c.userId];
    const theirImpact = liveStateEntries?.[c.userId]?.impact_remaining_units ?? 0;
    const cushion = you.score - c.score;
    const hasActivePlayers = summary && (summary.onField + summary.locker) > 0;
    return hasActivePlayers && theirImpact >= cushion && theirImpact > 0;
  });
  
  const biggestHunter = hunters.length > 0 
    ? hunters.reduce((biggest, c) => {
        const bImpact = liveStateEntries?.[biggest.userId]?.impact_remaining_units ?? 0;
        const cImpact = liveStateEntries?.[c.userId]?.impact_remaining_units ?? 0;
        return cImpact > bImpact ? c : biggest;
      })
    : null;
  
  // Build narrative
  let narrative = '';
  let isGood = false;
  let isBad = false;
  
  if (you.rank === 1) {
    if (biggestHunter) {
      const summary = summaries?.[biggestHunter.userId];
      const topPlayer = summary?.topPlayer;
      if (topPlayer && topPlayer.units >= 15) {
        const icon = getPlayerIcon(topPlayer.units);
        const where = topPlayer.section === 'onField' ? 'live' : 'waiting';
        narrative = `${biggestHunter.username} is hunting you — ${icon}${topPlayer.name} is ${where}`;
        isBad = true;
      } else {
        narrative = `${biggestHunter.username} is hunting you — stay sharp!`;
        isBad = true;
      }
    } else {
      narrative = "👑 You're the champ — no one can touch you!";
      isGood = true;
    }
  } else if (canCatchLeader && leader) {
    narrative = `You're hunting ${leader.username} — ${leaderGap} from taking 1st!`;
    isGood = true;
  } else if (bestReachable) {
    const gap = bestReachable.score - you.score;
    const pos = bestReachable.rank === 2 ? '2nd' : `#${bestReachable.rank}`;
    narrative = `You're hunting ${bestReachable.username} — ${gap} from taking ${pos}!`;
    isGood = true;
  } else if (biggestHunter) {
    const summary = summaries?.[biggestHunter.userId];
    const topPlayer = summary?.topPlayer;
    if (topPlayer && topPlayer.units >= 15) {
      const icon = getPlayerIcon(topPlayer.units);
      const where = topPlayer.section === 'onField' ? 'live' : 'waiting';
      narrative = `${biggestHunter.username} is hunting you — ${icon}${topPlayer.name} is ${where}`;
    } else {
      narrative = `${biggestHunter.username} is closing in — watch your back!`;
    }
    isBad = true;
  } else {
    narrative = `Holding at #${you.rank}`;
  }
  
  const bgColor = isGood ? 'bg-emerald-500/20' : isBad ? 'bg-red-500/15' : 'bg-slate-800';
  const textColor = isGood ? 'text-emerald-400' : isBad ? 'text-red-400' : 'text-slate-400';
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 z-40">
      <div className={`text-center py-2.5 px-4 text-[14px] font-bold leading-tight ${bgColor} ${textColor}`}>
        {narrative}
      </div>
      <div className="flex items-center justify-between px-4 py-3">
        <div className="text-center flex-1">
          <p className="text-[11px] font-bold text-slate-500 uppercase">Rank</p>
          <p className="text-xl font-black text-white">#{you.rank}</p>
        </div>
        <div className="text-center flex-1">
          <p className="text-[11px] font-bold text-slate-500 uppercase">Score</p>
          <p className="text-xl font-mono font-black text-white">{you.score}</p>
        </div>
        {you.rank !== 1 && (
          <div className="text-center flex-1">
            <p className="text-[11px] font-bold text-slate-500 uppercase">Gap</p>
            <p className="text-xl font-mono font-black text-red-400">-{leaderGap}</p>
          </div>
        )}
        <div className="text-center flex-1">
          <p className="text-[11px] font-bold text-slate-500 uppercase">Left</p>
          <p className="text-xl font-mono font-black text-emerald-400">{myImpact}</p>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// COACH ROWS - V9 Simplified Colors (TRUE TO MOCKUP)
// =============================================================================

/**
 * CoachAboveYou - Shows coaches ranked higher than you
 * V9: -{gap} with "in range" (green) or "out of reach" (red)
 * FINAL: Shows score + game plan results (X of Y hit)
 */
const CoachAboveYou = ({ coach, myImpact, summary, theirImpact, isFinal, onScout }) => {
  const isLeader = coach.rank === 1;
  const theirLead = coach.score - (coach.myScore || 0);
  
  // In FINAL state, everyone is finished
  const isFinished = isFinal || (summary && summary.onField === 0 && summary.locker === 0);
  
  // Can I catch them? Green = yes, Red = no (only matters if not finished)
  const canICatch = !isFinished && theirLead <= myImpact && myImpact > 0;
  
  let statusColor, tagline;
  if (isFinished) {
    statusColor = 'text-slate-400';
    tagline = `${coach.score} pts`;  // Show score in FINAL mode
  } else if (canICatch) {
    statusColor = 'text-emerald-400';
    tagline = 'in range';
  } else {
    statusColor = 'text-red-400';
    tagline = 'out of reach';
  }
  
  // Pass isFinal to buildPlayerInfo for game plan results
  const playerLines = buildPlayerInfo(summary, isFinal);
  
  return (
    <button 
      onClick={() => onScout(coach)}
      className={`w-full mx-4 mb-3 p-4 rounded-xl border text-left active:scale-[0.98] transition-transform ${
        isFinished
          ? 'bg-slate-900/50 border-slate-800/50'
          : canICatch 
            ? 'bg-emerald-900/10 border-emerald-500/30' 
            : 'bg-slate-900 border-slate-800'
      }`} 
      style={{width: 'calc(100% - 32px)'}}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-black text-slate-500">
            {isLeader && '👑'} #{coach.rank}
          </span>
          <span className={`text-[17px] font-bold ${isFinished ? 'text-slate-300' : 'text-white'}`}>
            {coach.username}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            {isFinal ? (
              <>
                <p className="text-[20px] font-mono font-black text-white">
                  {coach.score}
                </p>
                <p className="text-[12px] font-bold text-slate-500">points</p>
              </>
            ) : (
              <>
                <p className={`text-[20px] font-mono font-black ${statusColor}`}>
                  -{theirLead}
                </p>
                <p className={`text-[12px] font-bold ${statusColor}`}>{tagline}</p>
              </>
            )}
          </div>
          
          <div className="flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-800">
            <span className="text-[20px]">🎯</span>
          </div>
        </div>
      </div>
      
      {/* Player info / Game plan results */}
      <div className="space-y-1">
        {playerLines.map((line, i) => (
          <p key={i} className="text-[14px] text-slate-400">{line}</p>
        ))}
        {!isFinal && (
          <p className="text-[13px] text-slate-500">{theirImpact} impact left</p>
        )}
      </div>
    </button>
  );
};

/**
 * YouRow - V9 distinct styling
 * bg-slate-800 border-2 border-slate-600
 * FINAL: Shows score + game plan results
 */
const YouRow = ({ you, myImpact, summary, isFinal, onFieldReport }) => {
  // Pass isFinal to buildPlayerInfo for game plan results
  const playerLines = buildPlayerInfo(summary, isFinal);
  
  return (
    <button 
      onClick={onFieldReport}
      className="w-full mx-4 mb-3 p-4 rounded-xl bg-slate-800 border-2 border-slate-600 text-left active:scale-[0.98] transition-transform"
      style={{width: 'calc(100% - 32px)'}}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-black text-white">#{you.rank}</span>
          <span className="text-[17px] font-bold text-white">You</span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[20px] font-mono font-black text-white">{you.score}</p>
            <p className="text-[12px] font-bold text-slate-400">points</p>
          </div>
          
          <div className="flex items-center justify-center w-[44px] h-[44px] rounded-lg bg-slate-700">
            <span className="text-[20px]">📋</span>
          </div>
        </div>
      </div>
      
      {/* Player info / Game plan results */}
      <div className="space-y-1">
        {playerLines.map((line, i) => (
          <p key={i} className="text-[14px] text-slate-400">{line}</p>
        ))}
        {!isFinal && (
          <p className="text-[13px] text-emerald-400">{myImpact} impact left</p>
        )}
      </div>
    </button>
  );
};

/**
 * CoachBelowYou - Shows coaches ranked lower than you
 * V9: +{cushion} with "can catch you" (red), "not a threat" (green), "finished" (gray)
 * FINAL: Shows score + game plan results (always clickable to scout)
 */
const CoachBelowYou = ({ coach, myScore, summary, theirImpact, isFinal, onScout }) => {
  const cushion = myScore - coach.score;
  
  // Can they catch me? (only relevant in LIVE/PENDING)
  const canCatchMe = !isFinal && theirImpact >= cushion && theirImpact > 0;
  
  // Finished = FINAL state OR no active players
  const isFinished = isFinal || (summary && summary.onField === 0 && summary.locker === 0);
  
  let statusColor, tagline;
  if (isFinal) {
    statusColor = 'text-slate-400';
    tagline = `${coach.score} pts`;  // Show score in FINAL mode
  } else if (isFinished) {
    statusColor = 'text-slate-500';
    tagline = 'finished';
  } else if (canCatchMe) {
    statusColor = 'text-red-400';
    tagline = 'can catch you';
  } else {
    statusColor = 'text-emerald-400';
    tagline = 'not a threat';
  }
  
  // Pass isFinal to buildPlayerInfo for game plan results
  const playerLines = buildPlayerInfo(summary, isFinal);
  
  return (
    <button 
      onClick={() => onScout(coach)}  // Always clickable - user wants to see why they lost/won
      className={`w-full mx-4 mb-3 p-4 rounded-xl border text-left active:scale-[0.98] transition-transform ${
        isFinal
          ? 'bg-slate-900/50 border-slate-800/50'
          : isFinished 
            ? 'bg-slate-900/50 border-slate-800/50 opacity-60'
            : canCatchMe 
              ? 'bg-red-900/10 border-red-500/30' 
              : 'bg-slate-900 border-slate-800'
      }`} 
      style={{width: 'calc(100% - 32px)'}}
    >
      {/* Top row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-black text-slate-500">#{coach.rank}</span>
          <span className={`text-[17px] font-bold ${isFinal ? 'text-slate-300' : isFinished ? 'text-slate-500' : 'text-white'}`}>
            {coach.username}
          </span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-right">
            {isFinal ? (
              <>
                <p className="text-[20px] font-mono font-black text-white">
                  {coach.score}
                </p>
                <p className="text-[12px] font-bold text-slate-500">points</p>
              </>
            ) : (
              <>
                <p className={`text-[20px] font-mono font-black ${statusColor}`}>
                  +{cushion}
                </p>
                <p className={`text-[12px] font-bold ${statusColor}`}>{tagline}</p>
              </>
            )}
          </div>
          
          <div className={`flex items-center justify-center w-[44px] h-[44px] rounded-lg ${
            isFinished && !isFinal ? 'bg-slate-800/50' : 'bg-slate-800'
          }`}>
            <span className="text-[20px]">🎯</span>
          </div>
        </div>
      </div>
      
      {/* Player info / Game plan results */}
      <div className="space-y-1">
        {playerLines.map((line, i) => (
          <p key={i} className={`text-[14px] ${isFinished && !isFinal ? 'text-slate-500' : 'text-slate-400'}`}>{line}</p>
        ))}
        {!isFinal && !isFinished && (
          <p className={`text-[13px] ${canCatchMe ? 'text-red-400' : 'text-slate-500'}`}>
            {theirImpact} impact left
          </p>
        )}
      </div>
    </button>
  );
};

// =============================================================================
// LOADING / ERROR STATES
// =============================================================================

const LoadingState = () => (
  <div className="min-h-screen bg-slate-950 text-white font-sans flex items-center justify-center">
    <div className="text-center">
      <RefreshIcon spinning={true} />
      <p className="text-slate-400 text-sm mt-3">Loading scoreboard...</p>
    </div>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className="min-h-screen bg-slate-950 text-white font-sans flex flex-col items-center justify-center p-4">
    <p className="text-red-400 text-sm mb-4">{error}</p>
    <button 
      onClick={onRetry}
      className="px-4 py-2 bg-slate-800 rounded-lg text-white text-sm hover:bg-slate-700 min-h-[44px]"
    >
      Try Again
    </button>
  </div>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const ScoreboardScreen = () => {
  const navigate = useNavigate();
  const { poolId } = useParams();
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const { user } = useAuth();
  const myUserId = String(user?.user_id ?? user?.id ?? '').toLowerCase();
  
  // DATA LAYER 1: Leaderboard standings
  const { 
    standings: rawStandings, 
    poolInfo, 
    loading: standingsLoading, 
    error: standingsError, 
    refresh: refreshStandings,
    lastUpdated
  } = useLeaderboardPolling(poolId);
  
  // Build coach IDs from raw standings (before live-state dependency)
  const coachUserIds = useMemo(() => 
    rawStandings.map(s => s.userId || s.user_id).filter(Boolean), 
    [rawStandings]
  );
  
  // DATA LAYER 2: Live Summary (Phase 3.5) - moved before standings transform
  const {
    summaries,
    liveStateEntries,
    mode,
    loading: summaryLoading,
    refetch: refetchSummary,
  } = useLiveSummary(poolId, coachUserIds, { enablePolling: true });
  
  // Transform standings - override with live-state projected scores when available
  const standings = useMemo(() => {
    const hasLive = liveStateEntries && Object.keys(liveStateEntries).length > 0;
    
    const rows = rawStandings.map(s => {
      const uid = s.userId || s.user_id;
      const oddsUserId = String(uid || '').toLowerCase();
      const liveEntry = hasLive ? liveStateEntries[uid] : null;
      
      return {
        rank: s.rank ?? liveEntry?.rank,
        userId: uid,
        username: s.username || s.user_id || 'Unknown',
        score: s.score ?? s.current_balance ?? liveEntry?.projected_score ?? liveEntry?.score ?? 0,
        isMe: myUserId && oddsUserId === myUserId,
      };
    });
    
    // Re-sort by rank when using live data
    if (hasLive) {
      rows.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    }
    
    return rows;
  }, [rawStandings, myUserId, liveStateEntries]);
  
  // Derived data
  const myStanding = standings.find(s => s.isMe);
  const myScore = myStanding?.score || 0;
  const myImpact = liveStateEntries?.[myStanding?.userId]?.impact_remaining_units ?? 0;
  const coachCount = poolInfo?.totalEntrants || standings.length;
  
  // Split into above/below (V9 renders in rank order, but needs different row types)
  const coachesAbove = standings.filter(s => !s.isMe && s.score > myScore);
  const coachesBelow = standings.filter(s => !s.isMe && s.score < myScore);
  
  // Handle ties by rank
  const coachesTied = standings.filter(s => !s.isMe && s.score === myScore);
  coachesTied.forEach(c => {
    if (c.rank < (myStanding?.rank || 999)) {
      coachesAbove.push(c);
    } else {
      coachesBelow.push(c);
    }
  });
  
  // Sort by rank
  coachesAbove.sort((a, b) => a.rank - b.rank);
  coachesBelow.sort((a, b) => a.rank - b.rank);
  
  // Navigation handlers (LOCKED)
  const handleBack = () => navigate('/');
  
  const handleRefresh = () => {
    setIsRefreshing(true);
    refreshStandings();
    refetchSummary();
    setTimeout(() => setIsRefreshing(false), 1000);
  };
  
  const handleScout = (coach) => {
    navigate(`/standings/${poolId}/vs/${coach.userId}`);
  };
  
  const handleFieldReport = () => {
    navigate(`/pools/${poolId}/field-report`);
  };
  
  // Loading
  if (standingsLoading && !standings.length) {
    return <LoadingState />;
  }
  
  // Error
  if (standingsError && !standings.length) {
    return <ErrorState error={standingsError} onRetry={handleRefresh} />;
  }
  
  // Build "you" object
  const you = myStanding ? { ...myStanding } : null;
  
  // H2H PATTERN: Check if all coaches are resolved
  const allResolved = Object.keys(summaries).length > 0 && 
    Object.values(summaries).every(s => s.onField === 0 && s.locker === 0);
  const isHistorical = mode === 'historical';
  const isFinal = isHistorical || allResolved;
  const isLive = mode === 'live' && !allResolved;
  const isPending = mode === 'pending' && !allResolved;
  
  // Status bar (like H2H)
  const getStatusBar = () => {
    if (isFinal) {
      return { text: 'Final Results', color: 'bg-slate-800', textColor: 'text-slate-400' };
    }
    if (isLive) {
      return { text: '🔴 LIVE', color: 'bg-red-500/20', textColor: 'text-red-400', pulse: true };
    }
    if (isPending) {
      return { text: '⏳ Waiting for kickoff...', color: 'bg-amber-500/10', textColor: 'text-amber-400' };
    }
    return null;
  };
  
  const statusBar = getStatusBar();
  
  return (
    <div className="min-h-screen bg-slate-950 text-white font-sans">
      {/* HEADER - V9 */}
      <header className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white min-h-[44px]"
          >
            <BackIcon />
            <span className="text-[13px] font-bold uppercase tracking-wider">War Room</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-[15px] font-black text-white uppercase tracking-wider">Scoreboard</h1>
            <p className="text-[12px] text-slate-500">
              {coachCount} Coaches • {lastUpdated?.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) || '--'}
            </p>
          </div>
          
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="text-slate-400 hover:text-white min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
          >
            <RefreshIcon spinning={isRefreshing} />
          </button>
        </div>
      </header>
      
      {/* STATUS BAR - Like H2H */}
      {statusBar && (
        <div className={`px-3 py-2 border-b border-slate-800 text-center ${statusBar.color}`}>
          <span className={`text-[11px] font-semibold ${statusBar.textColor} ${statusBar.pulse ? 'animate-pulse' : ''}`}>
            {statusBar.text}
          </span>
        </div>
      )}
      
      {/* COACH ROWS - V9 (no section headers, just rank order) */}
      <div className="pb-36 pt-3">
        {standings.length === 0 ? (
          <div className="text-center py-12 px-4">
            <p className="text-slate-400 text-sm">No standings yet</p>
            <p className="text-slate-500 text-xs mt-1">Coaches will appear once rosters are locked</p>
          </div>
        ) : (
          <>
            {/* Coaches above you (in rank order) */}
            {coachesAbove.map(coach => (
              <CoachAboveYou
                key={coach.userId}
                coach={{ ...coach, myScore }}
                myImpact={myImpact}
                summary={summaries[coach.userId]}
                theirImpact={liveStateEntries?.[coach.userId]?.impact_remaining_units ?? 0}
                isFinal={isFinal}
                onScout={handleScout}
              />
            ))}
            
            {/* Your row */}
            {you && (
              <YouRow
                you={you}
                myImpact={myImpact}
                summary={summaries[you.userId]}
                isFinal={isFinal}
                onFieldReport={handleFieldReport}
              />
            )}
            
            {/* Coaches below you (in rank order) */}
            {coachesBelow.map(coach => (
              <CoachBelowYou
                key={coach.userId}
                coach={coach}
                myScore={myScore}
                summary={summaries[coach.userId]}
                theirImpact={liveStateEntries?.[coach.userId]?.impact_remaining_units ?? 0}
                isFinal={isFinal}
                onScout={handleScout}
              />
            ))}
            
            {/* Fallback if user not in standings */}
            {!myStanding && standings.map(coach => (
              <CoachAboveYou
                key={coach.userId}
                coach={{ ...coach, myScore: 0 }}
                myImpact={0}
                summary={summaries[coach.userId]}
                theirImpact={liveStateEntries?.[coach.userId]?.impact_remaining_units ?? 0}
                isFinal={isFinal}
                onScout={handleScout}
              />
            ))}
          </>
        )}
      </div>
      
      {/* FOOTER HUD - V9 Emotional */}
      {you && (
        <FooterHUD
          you={you}
          coaches={standings}
          summaries={summaries}
          liveStateEntries={liveStateEntries}
          mode={mode}
        />
      )}
    </div>
  );
};

export default ScoreboardScreen;
